import backendApi from '../axios/backendApi';
import { useDataStore } from '../stores/useDataStore';
import { useToast } from 'vue-toastification';

const toast = useToast();

const API_KEY = import.meta.env.VITE_API_KEY;
const headers = { 'X-API-Key': API_KEY };

export async function trainModelForCulture(t: (key: string) => string): Promise<void> {
  const dataStore = useDataStore();

  if (!dataStore.selectedCulture?.id) {
    toast.error(t('cultureNotSelected'));
    return;
  }

  try {
    const response = await backendApi.post('/predictions/train', {
      pbr_id: dataStore.selectedCulture.pbr.id,
      specie_id: dataStore.selectedCulture.specie.id
    }, { headers });

    const { status, mse, msg } = response.data;

    if (status === 'trained') {
      toast.success(t('modelTrainedSuccess') + (mse ? ` (MSE: ${mse.toFixed(3)})` : ''));
    } else if (status === 'skipped') {
      toast.warning(msg || t('modelSkipped'));
    } else {
      throw new Error('Statut de réponse inattendu');
    }
  } catch (error: any) {
    console.error('Erreur entraînement modèle:', error);
    let errorMsg = t('modelTrainError');
    if (error.response?.status === 401) {
      errorMsg = t('invalidApiKey');
    } else if (error.response?.status === 400) {
      errorMsg = t('trainError') + (error.response.data.msg || error.response.data.detail || '');
    } else if (error.response?.status === 404) {
      errorMsg = t('modelNotFoundError');
    } else if (error.response?.data?.detail) {
      errorMsg = t('trainError') + error.response.data.detail;
    }
    toast.error(errorMsg);
    throw error;
  }
}

export async function predictModelForCulture(
  culture_id: number,
  t: (key: string) => string
): Promise<Predictions | null> {
  try {
    const response = await backendApi.get(`/predictions/predict/${culture_id}`, { headers });

    // Extraction de l’objet interne "prediction" de la réponse
    const data = response.data;

    if (!data?.prediction) {
      toast.info(t('noPredictionData'));
      return null;
    }

    const predictionData: Predictions = {
      predicted_day: data.prediction.predicted_day,
      predicted_absorbance: data.prediction.predicted_absorbance,
      plafond_ratio: data.prediction.plafond_ratio,
      recent_plafond_ratio: data.prediction.recent_plafond_ratio,
      shap_explanation: data.prediction.shap_explanation ?? null,
    };

    toast.success(t('predictionSuccess'));
    return predictionData;

  } catch (error: any) {
    console.error('Erreur prédiction modèle:', error);

    let errorMsg = t('modelPredictError');
    if (error.response?.status === 401) {
      errorMsg = t('invalidApiKey');
    } else if (error.response?.status === 400 || error.response?.status === 404) {
      errorMsg = t('predictError') + (error.response.data.detail || error.response.data.msg || '');
    } else if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail;
    }

    toast.error(errorMsg);
    throw error;
    return null;
  }
}


export async function checkModelExists(specie_id: number, pbr_id: number, t: (key: string) => string): Promise<{ exists: boolean; metrics?: ModelMetrics }> {
  try {
    const response = await backendApi.get(`/predictions/check-model/${specie_id}/${pbr_id}`, { headers });

    const { exists, metrics } = response.data;

    return {
      exists: !!exists,
      metrics: metrics ? {
        mse: metrics.mse,
        r2: metrics.r2 ?? undefined,
        samples_train: metrics.samples_train,
        samples_test: metrics.samples_test,
        importances: metrics.importances || [],
        shap_importances: metrics.shap_importances || []
      } : undefined
    };
  } catch (error: any) {
    console.error('Erreur check modèle:', error);
    if (error.response?.status === 404) {
      return { exists: false, metrics: undefined };
    }
    let errorMsg = t('modelCheckError');
    if (error.response?.status === 401) {
      errorMsg = t('invalidApiKey');
    } else if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail;
    }
    toast.error(errorMsg);
    throw error;
  }
}

export async function analyzeCycles(culture_id: number, t: (key: string) => string): Promise<AnalyzeResponse | null> {
  try {
    const response = await backendApi.post('/predictions/analyze-cycles', { culture_id }, { headers });

    if (response.data.status !== 'analyzed') {
      throw new Error(response.data.msg || 'Analyse échouée');
    }

    toast.success(t('analyzeCyclesSuccess'));
    return response.data as AnalyzeResponse;
  } catch (error: any) {
    console.error('Erreur analyze cycles:', error);
    let errorMsg = t('modelAnalyzeCyclesError');
    if (error.response?.status === 401) {
      errorMsg = t('invalidApiKey');
    } else if (error.response?.status === 400) {
      errorMsg = t('analyzeCyclesError') + (error.response.data.detail || '');
    } else if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail;
    }
    toast.error(errorMsg);
    throw error;
  }
}

export async function monitorDriftForCulture(culture_id: number, t: (key: string) => string): Promise<DriftAnalysis | null> {
  try {
    const response = await backendApi.post('/predictions/monitor-drift', { culture_id }, { headers });

    const data = response.data;

    if (data.drift_detected) {
      toast.warning(t('driftDetected') + (data.recommend_retrain ? ` - ${t('recommendRetrain')}` : ''));
    }

    return {
      drift: data.drift_detected,
      p_value: data.p_value,
      recommend_retrain: data.recommend_retrain ?? data.recommendRetrain ?? false,
      features: data.features ?? data.recent_feature_stats,
      shap: data.shap ?? data.recent_shap_info
    };
  } catch (error: any) {
    console.error('Erreur monitor drift:', error);
    let errorMsg = t('driftMonitorError');
    if (error.response?.status === 401) {
      errorMsg = t('invalidApiKey');
    } else if (error.response?.status === 400) {
      errorMsg = t('driftError') + (error.response.data.detail || '');
    } else if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail;
    }
    toast.error(errorMsg);
    throw error;
  }
}